import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import dayjs from 'dayjs'

import type { IdentityStatusHistoryItem, PatientIdentityStatus } from '@/core/models/patient.model'
import type { Staff } from '@/core/models/staff.model'

import { IdentityStatusBadge } from './IdentityStatusBadge'

type IdentityStatusChangesTableProps = {
  history?: IdentityStatusHistoryItem[]
}

type IdentityStatusTransition = {
  changedAt: Date
  changedBy?: Staff
  nextStatus: PatientIdentityStatus
  previousStatus: PatientIdentityStatus
}

function buildStaffLabel(staff?: Staff): string {
  const userInfo = staff?.userInfo
  return `${userInfo?.surname ?? ''} ${userInfo?.name ?? ''}`.trim()
}

function buildIdentityStatusTransitions(history: IdentityStatusHistoryItem[] = []): IdentityStatusTransition[] {
  const items = history.filter(
    (item): item is IdentityStatusHistoryItem & { identityStatus: PatientIdentityStatus } => Boolean(item.identityStatus) && dayjs(item.changedAt).isValid()
  )

  return items.slice(0, -1).flatMap((item, index) => {
    const previousStatus = items[index + 1].identityStatus
    if (previousStatus === item.identityStatus) return []

    return [
      {
        changedAt: item.changedAt,
        changedBy: item.changedBy,
        nextStatus: item.identityStatus,
        previousStatus,
      },
    ]
  })
}

export function IdentityStatusChangesTable({ history }: IdentityStatusChangesTableProps) {
  const lingui = useLingui()
  const statusTransitions = buildIdentityStatusTransitions(history)

  if (statusTransitions.length === 0) {
    return (
      <Alert severity="info">
        <Trans>No identity status changes.</Trans>
      </Alert>
    )
  }

  return (
    <TableContainer>
      <Table size="small" aria-label={lingui._(t`Identity status changes`)}>
        <TableHead>
          <TableRow>
            <TableCell>
              <Trans>Date and time</Trans>
            </TableCell>
            <TableCell>
              <Trans>Staff</Trans>
            </TableCell>
            <TableCell>
              <Trans>Previous status</Trans>
            </TableCell>
            <TableCell>
              <Trans>New status</Trans>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {statusTransitions.map((transition) => (
            <TableRow key={`${dayjs(transition.changedAt).valueOf()}-${transition.nextStatus}`}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(transition.changedAt).format('DD/MM/YYYY HH:mm:ss')}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{buildStaffLabel(transition.changedBy) || '—'}</TableCell>
              <TableCell>
                <IdentityStatusBadge status={transition.previousStatus} />
              </TableCell>
              <TableCell>
                <IdentityStatusBadge status={transition.nextStatus} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
